import React, { FC } from 'react';
import { fmtHours, fmtMinutes, fmtPct } from '../scaling/scalingCard';
import { BaselineNote, ImpactChart, colours } from './ImpactChart';
import { ImpactTile } from './ImpactTile';
import { headlineWeeks } from './series';
import type { Granularity } from './series';
import type {
  BrowsingBucket,
  BrowsingPayload,
  IntentBucket,
  IntentsPayload,
} from './types';

/**
 * Sections 1–3 of the Browser Ladder tab: intent outcomes, browsing outcomes
 * and human work. Sections 1 and 3 read the same `intents` payload.
 */

export const fmtCount = (v: number | null) =>
  v == null
    ? 'No data yet'
    : v.toLocaleString(undefined, { maximumFractionDigits: 1 });

const definitionOf =
  (definitions: Record<string, string>) =>
  (key: string): string =>
    definitions[key] ?? 'No definition available.';

const intentFooter = (b: IntentBucket) =>
  `n = ${b.intents} intents · classified ${b.classified} · open ${b.open}`;

export const IntentOutcomes: FC<{
  data: IntentsPayload;
  granularity: Granularity;
}> = ({ data, granularity }) => {
  const buckets = data[granularity];
  const { week, prior } = headlineWeeks(data.weekly);
  const base = data.baseline.value;
  const definition = definitionOf(data.definitions);
  return (
    <>
      <div className='scaling-grid'>
        <ImpactTile
          index={1}
          title='Intent success rate'
          weekBucket={week?.bucket}
          current={week?.successRatePct ?? null}
          previous={prior?.successRatePct ?? null}
          baseline={base?.successRatePct ?? null}
          sampleN={week?.classified}
          previousSampleN={prior?.classified}
          sampleUnit='classified intents'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition('outcome')}
        />
        <ImpactTile
          index={2}
          title='Delivered or partially delivered'
          weekBucket={week?.bucket}
          current={week?.deliveredOrPartialPct ?? null}
          previous={prior?.deliveredOrPartialPct ?? null}
          baseline={base?.deliveredOrPartialPct ?? null}
          sampleN={week?.classified}
          previousSampleN={prior?.classified}
          sampleUnit='classified intents'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition('outcome')}
        />
        <ImpactTile
          index={3}
          title='Median time to success'
          weekBucket={week?.bucket}
          current={week?.medianHoursToSuccess ?? null}
          previous={prior?.medianHoursToSuccess ?? null}
          baseline={base?.medianHoursToSuccess ?? null}
          sampleN={week?.delivered}
          previousSampleN={prior?.delivered}
          sampleUnit='delivered'
          format={fmtHours}
          lowerIsBetter
          suffix='h'
          details={definition('timeToSuccess')}
        />
      </div>
      <div className='scaling-grid browser-ladder-charts'>
        <ImpactChart
          title='Intent success rate'
          definition={definition('outcome')}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.classified}
          tooltipFooter={intentFooter}
          yMax={100}
          series={[
            {
              label: 'Delivered',
              value: (b) => b.successRatePct,
              colour: colours.green,
              baseline: base?.successRatePct,
            },
            {
              label: 'Delivered or partial',
              value: (b) => b.deliveredOrPartialPct,
              colour: colours.blue,
            },
          ]}
        />
        <ImpactChart
          title='Time to success (hours)'
          definition={definition('timeToSuccess')}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.delivered}
          tooltipFooter={intentFooter}
          series={[
            {
              label: 'Median',
              value: (b) => b.medianHoursToSuccess,
              colour: colours.blue,
              baseline: base?.medianHoursToSuccess,
            },
            {
              label: '75th percentile',
              value: (b) => b.p75HoursToSuccess,
              colour: colours.purple,
            },
          ]}
        />
      </div>
      <BaselineNote label={data.baseline.label} />
    </>
  );
};

const browsingFooter = (b: BrowsingBucket) =>
  `n = ${b.tasks} tasks · resolved ${b.resolved} · waiting on user ${b.waiting}`;

export const BrowsingOutcomes: FC<{
  data: BrowsingPayload;
  granularity: Granularity;
}> = ({ data, granularity }) => {
  const buckets = data[granularity];
  const { week, prior } = headlineWeeks(data.weekly);
  const base = data.baseline.value;
  const definition = definitionOf(data.definitions);
  return (
    <>
      <div className='scaling-grid'>
        <ImpactTile
          index={1}
          title='Browsing success rate'
          weekBucket={week?.bucket}
          current={week?.successRatePct ?? null}
          previous={prior?.successRatePct ?? null}
          baseline={base?.successRatePct ?? null}
          sampleN={week?.resolved}
          previousSampleN={prior?.resolved}
          sampleUnit='resolved tasks'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition('outcome')}
        />
        <ImpactTile
          index={2}
          title='Stuck rate'
          weekBucket={week?.bucket}
          current={week?.stuckRatePct ?? null}
          previous={prior?.stuckRatePct ?? null}
          baseline={base?.stuckRatePct ?? null}
          sampleN={week?.resolved}
          previousSampleN={prior?.resolved}
          sampleUnit='resolved tasks'
          format={fmtPct}
          lowerIsBetter
          suffix='%'
          details={definition('outcome')}
        />
        <ImpactTile
          index={3}
          title='Median minutes to complete'
          weekBucket={week?.bucket}
          current={week?.medianMinutesToComplete ?? null}
          previous={prior?.medianMinutesToComplete ?? null}
          baseline={base?.medianMinutesToComplete ?? null}
          sampleN={week?.complete}
          previousSampleN={prior?.complete}
          sampleUnit='completed tasks'
          format={fmtMinutes}
          lowerIsBetter
          suffix='m'
          details={definition('duration')}
        />
      </div>
      <div className='scaling-grid browser-ladder-charts'>
        <ImpactChart
          title='Browsing outcomes'
          definition={definition('outcome')}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.resolved}
          tooltipFooter={browsingFooter}
          yMax={100}
          series={[
            {
              label: 'Complete',
              value: (b) => b.successRatePct,
              colour: colours.green,
            },
            {
              label: 'Complete or partial',
              value: (b) => b.completeOrPartialPct,
              colour: colours.blue,
            },
            {
              label: 'Stuck (failed, abandoned, handed to human)',
              value: (b) => b.stuckRatePct,
              colour: colours.red,
            },
          ]}
        />
        <ImpactChart
          title='Minutes to complete'
          definition={definition('duration')}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.complete}
          tooltipFooter={browsingFooter}
          series={[
            {
              label: 'Median',
              value: (b) => b.medianMinutesToComplete,
              colour: colours.blue,
            },
            {
              label: '75th percentile',
              value: (b) => b.p75MinutesToComplete,
              colour: colours.purple,
            },
          ]}
        />
      </div>
    </>
  );
};

export const HumanWork: FC<{
  data: IntentsPayload;
  granularity: Granularity;
}> = ({ data, granularity }) => {
  const buckets = data[granularity];
  const { week, prior } = headlineWeeks(data.weekly);
  const base = data.baseline.value;
  const definition = definitionOf(data.definitions);
  return (
    <>
      <div className='scaling-grid'>
        <ImpactTile
          index={1}
          title='Human tasks per 100 intents'
          weekBucket={week?.bucket}
          current={week?.humanTasksPer100Intents ?? null}
          previous={prior?.humanTasksPer100Intents ?? null}
          baseline={base?.humanTasksPer100Intents ?? null}
          sampleN={week?.intents}
          previousSampleN={prior?.intents}
          sampleUnit='intents'
          format={fmtCount}
          lowerIsBetter
          details={definition('humanWork')}
        />
        <ImpactTile
          index={2}
          title='Human minutes per intent'
          weekBucket={week?.bucket}
          current={week?.humanMinPerIntent ?? null}
          previous={prior?.humanMinPerIntent ?? null}
          baseline={base?.humanMinPerIntent ?? null}
          sampleN={week?.intents}
          previousSampleN={prior?.intents}
          sampleUnit='intents'
          format={fmtMinutes}
          lowerIsBetter
          suffix='m'
          details={definition('humanWork')}
        />
        <ImpactTile
          index={3}
          title='Intents needing no human'
          weekBucket={week?.bucket}
          current={week?.noHumanPct ?? null}
          previous={prior?.noHumanPct ?? null}
          baseline={base?.noHumanPct ?? null}
          sampleN={week?.intents}
          previousSampleN={prior?.intents}
          sampleUnit='intents'
          format={fmtPct}
          lowerIsBetter={false}
          suffix='%'
          details={definition('noHuman')}
        />
      </div>
      <div className='scaling-grid browser-ladder-charts'>
        <ImpactChart
          title='Human tasks per 100 intents'
          definition={definition('humanWork')}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.intents}
          tooltipFooter={intentFooter}
          series={[
            {
              label: 'Web actions',
              value: (b) => b.webActionsPer100Intents,
              colour: colours.amber,
            },
            {
              label: 'Browser interventions',
              value: (b) => b.interventionsPer100Intents,
              colour: colours.red,
            },
            {
              label: 'All human tasks',
              value: (b) => b.humanTasksPer100Intents,
              colour: colours.slate,
              baseline: base?.humanTasksPer100Intents,
            },
          ]}
        />
        <ImpactChart
          title='Less human help, same success'
          definition={definition('noHuman')}
          buckets={buckets}
          granularity={granularity}
          markers={data.releaseMarkers}
          sampleOf={(b) => b.intents}
          tooltipFooter={intentFooter}
          yMax={100}
          series={[
            {
              label: 'Intents needing no human (%)',
              value: (b) => b.noHumanPct,
              colour: colours.green,
              baseline: base?.noHumanPct,
            },
            {
              label: 'Intent success rate (%)',
              value: (b) => b.successRatePct,
              colour: colours.blue,
            },
          ]}
        />
      </div>
    </>
  );
};
